import { getFieldPath } from './recipientRequirements';
import { getCompanyName } from '../components/DashboardHeader/profileHelpers';
import { UserType } from '../enums';

const normalizeKey = (key) => String(key || '').replace(/[._-]/g, '').toLowerCase();

export function getRequirementFieldPath(req) {
  if (!req) return '';
  return req.key || req.field || req.data || req.variableName || '';
}

export function getRequirementFlatKey(req) {
  const path = getRequirementFieldPath(req);
  return path ? getFieldPath(path) : '';
}

export function collectRecipientFields(requirements = [], sections = []) {
  const seen = new Set();
  const fields = [];

  const addField = (req) => {
    const flatKey = getRequirementFlatKey(req);
    if (!flatKey || seen.has(flatKey)) return;
    seen.add(flatKey);
    fields.push(req);
  };

  requirements.forEach(addField);
  sections.forEach((section) => {
    if (section?.fields && Array.isArray(section.fields)) {
      section.fields.forEach(addField);
    }
  });

  return fields;
}

function pickAddress(user, isCompany) {
  if (isCompany) {
    const company = user?.company || {};
    return company.address || company.registeredAddress || company.operatingAddress || {};
  }
  return user?.userDetails?.address || {};
}

function buildIndividualProfile(user) {
  const details = user?.userDetails || {};
  const firstName = (details.firstName || user?.firstName || '').trim();
  const lastName = (details.lastName || user?.lastName || '').trim();
  const middleName = (details.middleName || '').trim();
  const title = (details.title || '').trim();
  const email = (user?.email || details.email || '').trim();
  const phoneNumber = (details.phoneNumber || '').trim();
  const parts = [firstName, middleName, lastName].filter(Boolean);
  const fullName = parts.join(' ').trim();
  const address = pickAddress(user, false);

  return {
    firstName,
    lastName,
    middleName,
    title,
    email,
    phoneNumber,
    name: fullName,
    displayName: fullName,
    beneficiary_name: fullName,
    beneficiaryName: fullName,
    address_line_1: address.line1 || '',
    address_line_2: address.line2 || '',
    city: address.city || '',
    state_province_region: address.stateRegionProvince || '',
    postal_code: address.postCode || address.postalCode || '',
    country: address.country || '',
    streetLine1: address.line1 || '',
    streetLine2: address.line2 || '',
    postalCode: address.postCode || address.postalCode || '',
    countryCode: address.country || '',
  };
}

function buildCompanyProfile(user) {
  const company = user?.company || {};
  const companyName = getCompanyName(company);
  const details = user?.userDetails || {};
  const firstName = (details.firstName || user?.firstName || '').trim();
  const lastName = (details.lastName || user?.lastName || '').trim();
  const middleName = (details.middleName || '').trim();
  const email = (user?.email || company.representativeEmail || company.email || '').trim();
  const address = pickAddress(user, true);

  return {
    companyName,
    name: companyName,
    displayName: companyName,
    beneficiary_name: companyName,
    beneficiaryName: companyName,
    firstName,
    lastName,
    middleName,
    companyEmail: email,
    email,
    address_line_1: address.line1 || '',
    address_line_2: address.line2 || '',
    city: address.city || '',
    state_province_region: address.stateRegionProvince || '',
    postal_code: address.postCode || address.postalCode || '',
    country: address.country || '',
    streetLine1: address.line1 || '',
    streetLine2: address.line2 || '',
    postalCode: address.postCode || address.postalCode || '',
    countryCode: address.country || '',
  };
}

const TAIL_ALIASES = {
  firstname: 'firstName',
  lastname: 'lastName',
  middlename: 'middleName',
  beneficiaryname: 'beneficiary_name',
  beneficiary: 'beneficiary_name',
  companyemail: 'companyEmail',
  streetline1: 'streetLine1',
  streetline2: 'streetLine2',
  addressline1: 'address_line_1',
  addressline2: 'address_line_2',
  stateprovinceregion: 'state_province_region',
  stateprovince: 'state_province_region',
  postalcode: 'postal_code',
  postcode: 'postal_code',
  zipcode: 'postal_code',
  countrycode: 'countryCode',
  phonenumber: 'phoneNumber',
  phone: 'phoneNumber',
};

function expandProfileLookup(profile) {
  const lookup = {};

  const assign = (key, value) => {
    if (value === null || value === undefined || value === '') return;
    const str = typeof value === 'string' ? value.trim() : value;
    if (str === '') return;
    lookup[key] = str;
    lookup[normalizeKey(key)] = str;
  };

  Object.entries(profile).forEach(([key, value]) => assign(key, value));

  Object.entries(TAIL_ALIASES).forEach(([normalizedTail, profileKey]) => {
    if (profile[profileKey] != null && profile[profileKey] !== '') {
      assign(normalizedTail, profile[profileKey]);
    }
  });

  return lookup;
}

function resolveValueForRequirement(req, lookup) {
  const path = getRequirementFieldPath(req);
  const flatKey = getRequirementFlatKey(req);
  if (!flatKey) return undefined;

  const candidates = [flatKey, path, path.split('.').pop()].filter(Boolean);

  for (let i = 0; i < candidates.length; i += 1) {
    const candidate = candidates[i];
    if (lookup[candidate] != null && lookup[candidate] !== '') {
      return lookup[candidate];
    }
    const normalized = normalizeKey(candidate);
    if (lookup[normalized] != null && lookup[normalized] !== '') {
      return lookup[normalized];
    }
    const aliasKey = TAIL_ALIASES[normalized];
    if (aliasKey && lookup[aliasKey] != null && lookup[aliasKey] !== '') {
      return lookup[aliasKey];
    }
    if (aliasKey && lookup[normalizeKey(aliasKey)] != null && lookup[normalizeKey(aliasKey)] !== '') {
      return lookup[normalizeKey(aliasKey)];
    }
  }

  return undefined;
}

export function canAutoFillRecipientFromUser(user, recipientType) {
  if (!user?.userType || !recipientType) return false;
  if (user.userType === UserType.Individual && recipientType === 'individual') return true;
  if (user.userType === UserType.Company && recipientType === 'company') return true;
  return false;
}

export function buildRecipientAutoFillFromUser(user, recipientType, requirements = [], sections = []) {
  if (!user || !canAutoFillRecipientFromUser(user, recipientType)) {
    return {};
  }

  const profile =
    recipientType === 'company' ? buildCompanyProfile(user) : buildIndividualProfile(user);
  const lookup = expandProfileLookup(profile);
  const autoFill = {};
  const allFields = collectRecipientFields(requirements, sections);

  allFields.forEach((req) => {
    const flatKey = getRequirementFlatKey(req);
    const value = resolveValueForRequirement(req, lookup);
    if (value !== undefined) {
      autoFill[flatKey] = value;
    }
  });

  return autoFill;
}
